import { Button, Table } from '@mairistem/composants';
import type { Exercice } from '@mairistem/comptabilite';
import { formatMontant } from '@mairistem/finances-utils';
import React from 'react';
import type Financement from '../../../interfaces/common/financement';
import type ProImputation from '../../../interfaces/common/proImputation';
import ProImputationItem from './ProImputationItem';

interface ProImputationListProps {
	decision: number;
	exercices: Exercice[];
	exercicesGestionTva: Set<number>;
	exercicesReportFonc: Set<number>;
	financement: Financement;
	onAddProImputation: () => void;
	onChangeProImputation: (
		data: Partial<ProImputation>,
		identifiantProImputation: number | string,
	) => void;
	onRemoveProImputation: (identifiantProImputation: number | string) => void;
}

const getRemainingMontantText = (montant: number) =>
	`(Restant: ${formatMontant(montant)})`;

const ProImputationList = ({
	decision,
	exercices,
	exercicesGestionTva,
	exercicesReportFonc,
	financement,
	onAddProImputation,
	onChangeProImputation,
	onRemoveProImputation,
}: ProImputationListProps) => {
	const exerciceHasGestionTva = React.useMemo(
		() => exercicesGestionTva.has(financement.exercice),
		[exercicesGestionTva, financement.exercice],
	);

	const exerciceHasReportFonc = React.useMemo(
		() => exercicesReportFonc.has(financement.exercice),
		[exercicesReportFonc, financement.exercice],
	);

	const montantsAImputer = React.useMemo(
		() =>
			financement.proImputations.reduce(
				(prev, curr) => ({
					montant: prev.montant - curr.montant,
					rarN: prev.rarN - curr.rarN,
					rarN1: prev.rarN1 - curr.rarN1,
					revision: prev.revision - curr.revision,
				}),
				{
					montant: financement.montant,
					rarN: financement.rarN,
					rarN1: financement.rarN1,
					revision: financement.revision,
				},
			),
		[
			financement.montant,
			financement.proImputations,
			financement.rarN,
			financement.rarN1,
			financement.revision,
		],
	);

	return (
		<Table singleLine>
			<Table.Header>
				<Table.Row>
					<Table.HeaderCell content="Ligne budgétaire" width={5} />
					<Table.HeaderCell
						content={`Crédit initial ${getRemainingMontantText(montantsAImputer.montant)}`}
						width={2}
					/>
					<Table.HeaderCell
						content={`Rééchelonnement ${getRemainingMontantText(montantsAImputer.revision)}`}
						width={2}
					/>
					<Table.HeaderCell
						content={`RAR ${financement.exercice ? financement.exercice : ''} ${getRemainingMontantText(montantsAImputer.rarN)}`}
						textAlign="right"
						width={2}
					/>
					<Table.HeaderCell
						content={`RAR ${financement.exercice ? financement.exercice - 1 : ''} ${getRemainingMontantText(montantsAImputer.rarN1)}`}
						textAlign="right"
						width={2}
					/>
					<Table.HeaderCell content="Engagé" textAlign="right" width={1} />
					<Table.HeaderCell content="Mandaté" textAlign="right" width={1} />
					<Table.HeaderCell content="Actions" textAlign="center" width={1} />
				</Table.Row>
			</Table.Header>
			<Table.Body>
				{financement.proImputations?.map((proImputation) => (
					<ProImputationItem
						decision={decision}
						exercice={financement.exercice}
						exercices={exercices}
						exerciceHasGestionTva={exerciceHasGestionTva}
						exerciceHasReportFonc={exerciceHasReportFonc}
						key={proImputation.identifiant}
						onChangeProImputation={(data) =>
							onChangeProImputation(data, proImputation.identifiant)
						}
						onRemoveProImputation={() =>
							onRemoveProImputation(proImputation.identifiant)
						}
						proImputation={proImputation}
						sens={financement.sens}
					/>
				))}
				<Table.Row>
					<Table.Cell colSpan={7} textAlign="center">
						<Button
							content="Ajouter une ligne"
							data-testid={`addProImputationButton${financement.identifiant}`}
							icon="plus"
							onClick={onAddProImputation}
							primary
							size="tiny"
						/>
					</Table.Cell>
				</Table.Row>
			</Table.Body>
		</Table>
	);
};

export default ProImputationList;
